<div class="voilation-report-container">

  <!-- Header -->
  <div class="page-header mb-3">
    <div>
      <h3 class="title mb-1">Violation Reports</h3>
      <p class="subtitle page-desc mb-0">Review and manage users reported for policy violations.</p>
    </div>
  </div>

  <!-- Stats Cards -->
  <div class="row g-3 mb-4">
    @for (stat of stats(); track stat.label) {
      <div class="col-md-3">
        <div class="card header-card border-0 shadow h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="stat-icon me-3" [style.background-color]="stat.color + '20'">
                <i [class]="stat.icon" [style.color]="stat.color"></i>
              </div>
              <div>
                <h3 class="mb-0 fw-bold" [countUp]="stat.count">0</h3>
                <p class="text-muted mb-0 small">{{ stat.label }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Search row -->
  <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
    <div class="d-flex align-items-center gap-2">
      <app-search-box tooltipText="Search By reporter/reported user's firstName, lastName, and email" [value]="searchValue()" (search)="onSearch($event)"></app-search-box>
      <p-select
        [options]="reportReasonOptions"
        [(ngModel)]="reportReasonFilter"
        (ngModelChange)="onReportReasonFilterChange()"
        optionLabel="label"
        optionValue="value"
        optionDisabled="disabled"
        [showClear]="true"
        placeholder="Report Reason"
        class="filter-select"
        [disabled]="loading()">
      </p-select>
      @if (hasSelection()) {
        <button class="btn btn-icon btn-delete-bulk" (click)="deleteSelectedReports()">
          <i class="pi pi-trash" style="font-size: 1.25rem;"></i>
        </button>
      }
    </div>
    <div class="d-flex align-items-center gap-3 ms-auto">
      <div class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper">
        <button type="button" class="btn btn-icon p-1 header-icon-action-btn" (click)="currentPage.set(1); fetchReports()" title="Refresh">
          <i class="pi pi-refresh" style="font-size: 1.25rem; color: #6b7280;"></i>
        </button>
        <button type="button" class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn" (click)="toggleIncludeDeleted()">
          <i class="pi pi-asterisk" style="font-size: 1.25rem;" [style.color]="includeDeleted() ? '#F908B7' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Table Section -->
  <div class="table-container shadow">
    <table class="violation-table">
      <thead>
        <tr>
          <th class="checkbox-col">
            <input type="checkbox" [checked]="allSelected()" (change)="toggleSelectAll()" />
          </th>
          <th class="sortable" (click)="onSort('user')">
            USER <span class="sort-indicator" [class.active]="currentSortField() === 'user'">{{ getSortIcon('user') }}</span>
          </th>
          <th class="sortable" (click)="onSort('createdAt')">
            DATE <span class="sort-indicator" [class.active]="currentSortField() === 'createdAt'">{{ getSortIcon('createdAt') }}</span>
          </th>
          <th class="sortable" (click)="onSort('category')">
            REPORT REASON <span class="sort-indicator" [class.active]="currentSortField() === 'category'">{{ getSortIcon('category') }}</span>
          </th>
          <th class="sortable" (click)="onSort('reports')">
            REPORTS <span class="sort-indicator" [class.active]="currentSortField() === 'reports'">{{ getSortIcon('reports') }}</span>
          </th>
          <th class="sortable" (click)="onSort('status')">
            STATUS <span class="sort-indicator" [class.active]="currentSortField() === 'status'">{{ getSortIcon('status') }}</span>
          </th>
          <th class="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        @if (loading()) {
          <tr>
            <td colspan="100%">
              <app-spinner-loader [width]="35" />
            </td>
          </tr>
        } @else {
          @if (reports().length === 0) {
            <tr>
              <td colspan="100%" class="no-data-cell">No violation reports found</td>
            </tr>
          }
          @for (report of reports(); track report._id) {
            <tr [ngClass]="{'deleted-row': !!report.deletedAt}" [class.selected]="report.selected">
              <td class="checkbox-col">
                <input type="checkbox" [(ngModel)]="report.selected" (change)="updateSelectAllState()" />
              </td>

              <td>
                <div class="d-flex align-items-center gap-3">
                  <img [src]="report.reportedUserInfo.avatar || 'assets/images/profile.png'" [alt]="report.reportedUserInfo.firstName" class="rounded-circle" width="40" height="40" style="flex-shrink: 0;" />
                  <div class="tooltip-wrap">
                    <div class="fw-semibold text-clip">{{ report.reportedUserInfo.firstName }} {{ report.reportedUserInfo.lastName }}</div>
                    <div class="text-muted small email-clip">{{ report.reportedUserInfo.email }}</div>
                    <div class="tooltip-box text-start">
                      <div><strong>{{ report.reportedUserInfo.firstName }} {{ report.reportedUserInfo.lastName }}</strong></div>
                      <div>{{ report.reportedUserInfo.email }}</div>
                    </div>
                  </div>
                </div>
              </td>
              <td class="text-muted">{{ report.createdAt | date:'shortDate' }}</td>
              <td>
                <p-tag [value]="report.category" [styleClass]="getReasonSeverity(report.category)" [rounded]="true" />
              </td>
              <td>
                <span class="badge bg-warning text-dark">{{ report.reportedUserInfo.totalReports || 1 }} Reports</span>
              </td>
              <td>
                <p-tag [value]="report.status" [styleClass]="getStatusSeverity(report.status)" [rounded]="true" />
              </td>
              <td class="text-center">
                <button class="btn btn-view" (click)="onAction('view', report)">View</button>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>Showing {{ startIndex }} to {{ endIndex }} of {{ totalCount() }} Entries</span>
      </div>
      <div class="d-flex gap-2">
        <label class="page-size-label" for="pageSizeSelect">Page Size:</label>
        <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)"
          class="form-select" style="width: 70px;">
          @for (size of pageSizes; track size) {
            <option [value]="size">
              {{ size }}
            </option>
          }
        </select>
        <button class="btn btn-outline pagination-btn"
          [disabled]="currentPage() === 1" (click)="previousPage()">
          &lt; Prev
        </button>
        <button class="btn btn-outline pagination-btn"
          [disabled]="currentPage() === totalPages" (click)="nextPage()">
          Next &gt;
        </button>
      </div>
    </div>
  </div>

  @if (isViewDetailsOpen() && selectedReport()) {
    <app-voilation-details-modal
      [(visible)]="isViewDetailsOpen"
      [report]="selectedReport()"
      (onClose)="closeViewDetails()"
      (onAction)="onModalAction($event)"
      />
    }
  </div>
